import { chainId, blockExplorer } from '../constants';

export interface NetworkConfig {
  chainId: string;
  explorerUrl: string;
  name: string;
}

// Network configurations for different chains
const NETWORK_CONFIGS: Record<string, NetworkConfig> = {
  // Swarm devnet
  '0x142F55': {
    chainId: '0x142F55',
    explorerUrl: 'https://explorer.devnet.swarmind.ai/',
    name: 'Swarm Devnet',
  },
  // Ethereum mainnet (fallback)
  '0x1': {
    chainId: '0x1',
    explorerUrl: 'https://etherscan.io/',
    name: 'Ethereum Mainnet',
  },
  // Sepolia testnet (fallback)
  '0xaa36a7': {
    chainId: '0xaa36a7',
    explorerUrl: 'https://sepolia.etherscan.io/',
    name: 'Sepolia Testnet',
  },
};

class ExplorerService {
  /**
   * Get current network configuration
   */
  private getCurrentNetwork(): NetworkConfig {
    // Try to get current network from MetaMask
    if (typeof window !== 'undefined' && window.ethereum) {
      const currentChainId = (window.ethereum as any).chainId;
      if (currentChainId && NETWORK_CONFIGS[currentChainId]) {
        return NETWORK_CONFIGS[currentChainId];
      }
    }

    // Default to Swarm devnet
    return NETWORK_CONFIGS[chainId] || {
      chainId,
      explorerUrl: blockExplorer,
      name: 'Swarm Devnet',
    };
  }

  /**
   * Generate explorer URL for a transaction hash
   */
  getTransactionUrl(transactionHash: string, networkChainId?: string): string {
    try {
      const network = networkChainId && NETWORK_CONFIGS[networkChainId] 
        ? NETWORK_CONFIGS[networkChainId]
        : this.getCurrentNetwork();

      // Ensure the explorer URL ends with a slash
      const baseUrl = network.explorerUrl.endsWith('/') 
        ? network.explorerUrl 
        : `${network.explorerUrl}/`;

      return `${baseUrl}tx/${transactionHash}`;
    } catch (error) {
      console.error('Error generating explorer URL:', error);
      // Fallback to Swarm devnet explorer
      return `${blockExplorer}tx/${transactionHash}`;
    }
  }

  /**
   * Generate explorer URL for an address
   */
  getAddressUrl(address: string, networkChainId?: string): string {
    try {
      const network = networkChainId && NETWORK_CONFIGS[networkChainId] 
        ? NETWORK_CONFIGS[networkChainId]
        : this.getCurrentNetwork();

      const baseUrl = network.explorerUrl.endsWith('/') 
        ? network.explorerUrl 
        : `${network.explorerUrl}/`;

      return `${baseUrl}address/${address}`;
    } catch (error) {
      console.error('Error generating address URL:', error);
      return `${blockExplorer}address/${address}`;
    }
  }

  /**
   * Generate explorer URL for a block
   */
  getBlockUrl(blockNumber: number | string, networkChainId?: string): string {
    try {
      const network = networkChainId && NETWORK_CONFIGS[networkChainId] 
        ? NETWORK_CONFIGS[networkChainId]
        : this.getCurrentNetwork();

      const baseUrl = network.explorerUrl.endsWith('/') 
        ? network.explorerUrl 
        : `${network.explorerUrl}/`;

      return `${baseUrl}block/${blockNumber}`;
    } catch (error) {
      console.error('Error generating block URL:', error);
      return `${blockExplorer}block/${blockNumber}`;
    }
  }

  /**
   * Get network name for display
   */
  getNetworkName(networkChainId?: string): string {
    try {
      const network = networkChainId && NETWORK_CONFIGS[networkChainId] 
        ? NETWORK_CONFIGS[networkChainId]
        : this.getCurrentNetwork();

      return network.name;
    } catch (error) {
      console.error('Error getting network name:', error);
      return 'Unknown Network';
    }
  }

  /**
   * Check if a network is supported
   */
  isNetworkSupported(networkChainId: string): boolean {
    return !!NETWORK_CONFIGS[networkChainId];
  }

  /**
   * Get all supported networks
   */
  getSupportedNetworks(): NetworkConfig[] {
    return Object.values(NETWORK_CONFIGS);
  }

  /**
   * Validate transaction hash format
   */
  isValidTransactionHash(hash: string): boolean {
    return /^0x[a-fA-F0-9]{64}$/.test(hash);
  }

  /**
   * Open transaction in explorer (new tab)
   */
  openTransaction(transactionHash: string, networkChainId?: string): void {
    if (!this.isValidTransactionHash(transactionHash)) {
      console.warn('Invalid transaction hash format:', transactionHash);
      return;
    }

    const url = this.getTransactionUrl(transactionHash, networkChainId);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Open address in explorer (new tab)
   */
  openAddress(address: string, networkChainId?: string): void {
    const url = this.getAddressUrl(address, networkChainId);
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

export const explorerService = new ExplorerService();